<template>
  <div class="best-player-voting">
    <!-- Header with back -->
 

    <!-- List of polls -->
    <div class="bpv-polls" v-if="!selectedPollId">
      <div v-if="isAdmin" class="bpv-create-row">
        <a-button type="primary" @click="showCreateModal" block>
          <template #icon><PlusOutlined /></template>
          Создать голосование
        </a-button>
      </div>

      <a-empty v-if="!loadingPolls && visiblePolls.length === 0" description="Нет голосований" />
      <a-spin v-else-if="loadingPolls" />
      <div v-else class="bpv-poll-list">
        <div
          v-for="p in visiblePolls"
          :key="p.id"
          class="bpv-poll-card"
          @click="openPoll(p.id)"
        >
          <div class="bpv-poll-title">
            {{ p.title || formatEventLabel(p) || 'Голосование за лучшего игрока' }}
          </div>
          <div class="bpv-poll-meta">
            <a-tag :color="p.status === 'open' ? 'green' : 'default'">
              {{ p.status === 'open' ? 'Открыто' : 'Закрыто' }}
            </a-tag>
            <span class="bpv-poll-votes">Проголосовало: {{ p.votes_count || 0 }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Poll detail: vote or results -->
    <div class="bpv-poll-detail" v-else>
      <div v-if="loadingPoll" class="bpv-loading"><a-spin size="large" /></div>
      <div v-else-if="pollData">
        <div class="bpv-poll-header">
          <h4>{{ pollData.title || formatEventLabel(pollData) || 'Голосование за лучшего игрока' }}</h4>
          <a-tag :color="pollData.status === 'open' ? 'green' : 'default'">
            {{ pollData.status === 'open' ? 'Открыто' : 'Закрыто' }}
          </a-tag>
        </div>

        <!-- Closed poll, no access to results -->
        <div v-if="pollData.status === 'closed' && !pollData.can_see_results" class="bpv-closed-no-access">
          <p>Голосование закрыто. Результаты доступны только администратору голосования.</p>
        </div>

        <!-- Results (if authorized and poll closed or user wants to see) -->
        <div v-else-if="pollData.can_see_results && (pollData.status === 'closed' || showResults)" class="bpv-results">
          <h4>Результаты</h4>
          <div v-if="loadingResults" class="bpv-loading"><a-spin /></div>
          <div v-else-if="voteResults" class="bpv-results-list">
            <div
              v-for="(r, idx) in voteResults.results"
              :key="r.player_id"
              class="bpv-result-row"
            >
              <span class="bpv-result-rank">{{ idx + 1 }}</span>
              <span class="bpv-result-name">{{ r.last_name }} {{ r.first_name }}</span>
              <span v-if="r.number" class="bpv-result-number">#{{ r.number }}</span>
              <span class="bpv-result-votes">{{ r.votes_count }} {{ pluralize(r.votes_count) }}</span>
            </div>
            <div class="bpv-total">Всего голосов: {{ voteResults.total_votes }}</div>
          </div>
          <a-button v-if="pollData.status === 'open' && !showResults" type="link" @click="loadResults">
            Показать результаты
          </a-button>
        </div>

        <!-- Vote form (if poll is open) -->
        <div v-else-if="pollData.status === 'open'" class="bpv-vote">
          <p class="bpv-vote-hint">Выберите лучшего игрока:</p>
          <div class="bpv-candidates">
            <div
              v-for="c in voteableCandidates"
              :key="c.id"
              class="bpv-candidate"
              :class="{ selected: pollData.my_vote_player_id === c.id }"
              @click="vote(c.id)"
            >
              <a-avatar :src="c.photo_url" :size="40">
                <template #icon><UserOutlined /></template>
              </a-avatar>
              <div class="bpv-candidate-info">
                <span class="bpv-candidate-name">{{ c.last_name }} {{ c.first_name }}</span>
                <span v-if="c.number" class="bpv-candidate-number">#{{ c.number }}</span>
              </div>
              <CheckCircleOutlined v-if="pollData.my_vote_player_id === c.id" class="bpv-check" />
            </div>
          </div>
          <a-button v-if="pollData.can_see_results && !showResults" type="link" @click="loadResults" style="margin-top: 12px;">
            Показать результаты (админ)
          </a-button>
        </div>

        <div class="bpv-actions">
          <a-button @click="backToList">← Назад к списку</a-button>
          <a-button
            v-if="isAdmin && pollData.status === 'open'"
            type="primary"
            danger
            @click="closePoll"
          >
            Закрыть голосование
          </a-button>
        </div>
      </div>
    </div>

    <!-- Create poll modal -->
    <a-modal
      v-model:open="createModalVisible"
      title="Создать голосование"
      :confirm-loading="creatingPoll"
      @ok="createPoll"
      @cancel="createModalVisible = false"
    >
      <a-form layout="vertical">
        <a-form-item label="Событие (опционально)">
          <a-select
            v-model:value="newPoll.event_id"
            placeholder="Привязать к событию или оставить пустым"
            allow-clear
          >
            <a-select-option v-for="e in upcomingEvents" :key="e.id" :value="e.id">
              {{ formatEventShort(e) }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="Название">
          <a-input v-model:value="newPoll.title" placeholder="Например: Игра против Спартака" />
        </a-form-item>
        <a-form-item label="Игроки, исключённые из голосования">
          <span class="bpv-exclude-hint">Выберите участников, которые не участвуют как кандидаты</span>
          <div class="bpv-exclude-actions">
            <a-button size="small" type="link" @click="excludeAllPlayers">Выбрать всех</a-button>
            <a-button size="small" type="link" @click="excludeNoPlayers">Убрать всех</a-button>
          </div>
          <a-select
            v-model:value="newPoll.excluded_player_ids"
            mode="multiple"
            placeholder="Выберите игроков"
            style="width: 100%; margin-top: 8px;"
          >
            <a-select-option v-for="p in allTeamPlayers" :key="p.id" :value="p.id">
              {{ p.last_name }} {{ p.first_name }}{{ p.number ? ' #' + p.number : '' }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="Администратор голосования (видит результаты)">
          <a-select
            v-model:value="newPoll.admin_player_id"
            placeholder="Кто может видеть результаты (по умолчанию — админ команды)"
            allow-clear
          >
            <a-select-option v-for="p in allTeamPlayers" :key="p.id" :value="p.id">
              {{ p.last_name }} {{ p.first_name }} {{ p.number ? '#' + p.number : '' }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { PlusOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons-vue';
import { api } from '../services/api';

export default {
  name: 'BestPlayerVoting',
  components: { PlusOutlined, UserOutlined, CheckCircleOutlined },
  props: {
    teamId: { type: Number, required: true },
    isAdmin: { type: Boolean, default: false },
    teamPlayers: { type: Array, default: () => [] },
    teamEvents: { type: Array, default: () => [] },
  },
  emits: ['back'],
  data() {
    return {
      polls: [],
      loadingPolls: false,
      selectedPollId: null,
      pollData: null,
      loadingPoll: false,
      voteResults: null,
      loadingResults: false,
      showResults: false,
      createModalVisible: false,
      creatingPoll: false,
      newPoll: {
        event_id: null,
        title: '',
        excluded_player_ids: [],
        admin_player_id: null,
      },
    };
  },
  computed: {
    voteableCandidates() {
      const candidates = this.pollData?.candidates || [];
      const myPlayerId = this.pollData?.current_user_player_id;
      if (!myPlayerId) return candidates;
      return candidates.filter((c) => c.id !== myPlayerId);
    },
    visiblePolls() {
      return (this.polls || []).filter((p) => p.status === 'open' || p.can_see_results);
    },
    allTeamPlayers() {
      const flat = [];
      const byPos = this.teamPlayers?.players || {};
      for (const pos of ['forward', 'defender', 'goalkeeper', 'coach', 'service']) {
        for (const p of byPos[pos] || []) {
          flat.push(p);
        }
      }
      return flat.sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`));
    },
    upcomingEvents() {
      const now = new Date().toISOString().slice(0, 10);
      return (this.teamEvents || [])
        .filter((e) => e.date >= now)
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
        .slice(0, 30);
    },
  },
  watch: {
    teamId() {
      this.loadPolls();
      this.selectedPollId = null;
      this.pollData = null;
    },
  },
  mounted() {
    this.loadPolls();
  },
  methods: {
    async loadPolls() {
      if (!this.teamId) return;
      this.loadingPolls = true;
      try {
        const res = await api.getBestPlayerVotingPolls(this.teamId);
        this.polls = res.polls || [];
      } catch (e) {
        this.$message?.error(e.message || 'Ошибка загрузки голосований');
      } finally {
        this.loadingPolls = false;
      }
    },
    formatEventLabel(p) {
      if (p.event_type && p.event_date) {
        const d = new Date(p.event_date);
        const dStr = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
        return p.event_type === 'game' && p.event_opponent
          ? `Игра: ${p.event_opponent} (${dStr})`
          : `${p.event_type === 'game' ? 'Игра' : 'Тренировка'} ${dStr}`;
      }
      return null;
    },
    formatEventShort(e) {
      if (!e) return '';
      const d = new Date(e.date);
      const dStr = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
      return e.type === 'game' && e.opponent
        ? `Игра: ${e.opponent} (${dStr})`
        : `${e.type === 'game' ? 'Игра' : 'Тренировка'} ${dStr}`;
    },
    formatDate(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    pluralize(n) {
      const forms = ['голос', 'голоса', 'голосов'];
      const i = n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
      return forms[i];
    },
    async openPoll(pollId) {
      this.selectedPollId = pollId;
      this.pollData = null;
      this.voteResults = null;
      this.showResults = false;
      this.loadingPoll = true;
      try {
        this.pollData = await api.getBestPlayerVotingPoll(this.teamId, pollId);
        if (this.pollData.status === 'closed' && this.pollData.can_see_results) {
          await this.loadResults();
        }
      } catch (e) {
        this.$message?.error(e.message || 'Ошибка загрузки голосования');
        this.selectedPollId = null;
      } finally {
        this.loadingPoll = false;
      }
    },
    async vote(playerId) {
      if (!this.pollData || this.pollData.status !== 'open') return;
      try {
        await api.voteBestPlayer(this.teamId, this.selectedPollId, playerId);
        this.pollData = { ...this.pollData, my_vote_player_id: playerId };
        this.$message?.success('Голос учтён');
      } catch (e) {
        this.$message?.error(e.message || 'Ошибка голосования');
      }
    },
    async loadResults() {
      this.loadingResults = true;
      try {
        this.voteResults = await api.getBestPlayerVotingResults(this.teamId, this.selectedPollId);
        this.showResults = true;
      } catch (e) {
        this.$message?.error(e.message || 'Нет доступа к результатам');
      } finally {
        this.loadingResults = false;
      }
    },
    async closePoll() {
      try {
        await api.closeBestPlayerVotingPoll(this.teamId, this.selectedPollId);
        this.pollData = { ...this.pollData, status: 'closed' };
        this.$message?.success('Голосование закрыто');
        await this.loadResults();
      } catch (e) {
        this.$message?.error(e.message || 'Ошибка');
      }
    },
    backToList() {
      this.selectedPollId = null;
      this.pollData = null;
      this.voteResults = null;
      this.showResults = false;
      this.loadPolls();
    },
    excludeAllPlayers() {
      this.newPoll.excluded_player_ids = this.allTeamPlayers.map((p) => p.id);
    },
    excludeNoPlayers() {
      this.newPoll.excluded_player_ids = [];
    },
    showCreateModal() {
      this.newPoll = {
        event_id: null,
        title: '',
        excluded_player_ids: [],
        admin_player_id: null,
      };
      this.createModalVisible = true;
    },
    async createPoll() {
      this.creatingPoll = true;
      try {
        await api.createBestPlayerVotingPoll(this.teamId, {
          event_id: this.newPoll.event_id || undefined,
          title: this.newPoll.title || undefined,
          excluded_player_ids: this.newPoll.excluded_player_ids || [],
          admin_player_id: this.newPoll.admin_player_id || undefined,
        });
        this.$message?.success('Голосование создано');
        this.createModalVisible = false;
        await this.loadPolls();
      } catch (e) {
        this.$message?.error(e.message || 'Ошибка создания голосования');
      } finally {
        this.creatingPoll = false;
      }
    },
  },
};
</script>

<style scoped>
.best-player-voting {
  padding: 16px;
}

.bpv-header h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
}

.bpv-create-row {
  margin-bottom: 16px;
}

.bpv-poll-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bpv-poll-card {
  padding: 12px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
}

.bpv-poll-card:hover {
  border-color: #1890ff;
  background: #f0f7ff;
}

.bpv-poll-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.bpv-poll-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8c8c8c;
}

.bpv-poll-detail {
  margin-top: 16px;
}

.bpv-poll-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.bpv-poll-header h4 {
  margin: 0;
  font-size: 16px;
}

.bpv-loading {
  padding: 24px;
  text-align: center;
}

.bpv-vote-hint {
  margin-bottom: 12px;
  color: #595959;
}

.bpv-candidates {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bpv-candidate {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
}

.bpv-candidate:hover {
  border-color: #1890ff;
}

.bpv-candidate.selected {
  border-color: #52c41a;
  background: #f6ffed;
}

.bpv-candidate-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.bpv-candidate-name {
  font-weight: 500;
}

.bpv-candidate-number {
  font-size: 12px;
  color: #8c8c8c;
}

.bpv-check {
  color: #52c41a;
  font-size: 18px;
}

.bpv-results h4 {
  margin: 0 0 12px 0;
}

.bpv-results-list {
  margin-bottom: 12px;
}

.bpv-result-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.bpv-result-rank {
  font-weight: 600;
  min-width: 24px;
}

.bpv-result-votes {
  margin-left: auto;
  color: #8c8c8c;
}

.bpv-total {
  margin-top: 12px;
  font-weight: 600;
  padding-top: 12px;
  border-top: 1px solid #e8e8e8;
}

.bpv-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
}

.bpv-exclude-hint {
  display: block;
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
}

.bpv-exclude-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
  margin-bottom: 4px;
}

.bpv-closed-no-access {
  padding: 24px 0;
  text-align: center;
  color: #8c8c8c;
}

.bpv-closed-no-access p {
  margin-bottom: 16px;
}
</style>
